import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const fetchFinancements = createAsyncThunk(
  "financements/fetchAll",
  async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
    try {
      const r = await api.get('/Financement', { params: { page, pageSize } });
      return r.data;
    } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
  }
);

export const fetchDashboard = createAsyncThunk(
  "financements/dashboard",
  async (_, { rejectWithValue }) => {
    try {
      const r = await api.get('/Financement/dashboard');
      return r.data;
    } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
  }
);

export const createFinancement = createAsyncThunk(
  "financements/create",
  async (data, { rejectWithValue }) => {
    try {
      await api.post('/Financement', data);
      const list = await api.get('/Financement', { params: { page: 1, pageSize: 10 } });
      return list.data;
    } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
  }
);

export const deleteFinancement = createAsyncThunk(
  "financements/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/Financement/${id}`);
      return id;
    } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
  }
);

export const payerTraite = createAsyncThunk(
  "financements/payerTraite",
  async ({ financementId, traiteId }, { rejectWithValue }) => {
    try {
      await api.patch(`/Financement/${financementId}/traites/${traiteId}/payer`);
      const r = await api.get(`/Financement/${financementId}`);
      return r.data;
    } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
  }
);

// Paie plusieurs traites en séquence puis rafraîchit le financement
export const payerPlusieursTraites = createAsyncThunk(
  "financements/payerPlusieurs",
  async ({ financementId, traiteIds }, { rejectWithValue }) => {
    try {
      for (const traiteId of traiteIds) {
        await api.patch(`/Financement/${financementId}/traites/${traiteId}/payer`);
      }
      const r = await api.get(`/Financement/${financementId}`);
      return r.data;
    } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
  }
);

const slice = createSlice({
  name: "financements",
  initialState: {
    items: [],
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    dashboard: null,
    loading: false,
    dashboardLoading: false,
    error: null,
    success: false,
    successMessage: null,
  },
  reducers: {
    resetSuccess: (state) => { state.success = false; state.successMessage = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFinancements.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFinancements.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchFinancements.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(fetchDashboard.pending, (state) => { state.dashboardLoading = true; })
      .addCase(fetchDashboard.fulfilled, (state, action) => { state.dashboardLoading = false; state.dashboard = action.payload; })
      .addCase(fetchDashboard.rejected, (state) => { state.dashboardLoading = false; });

    builder
      .addCase(createFinancement.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createFinancement.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = 1;
        state.success = true;
        state.successMessage = 'create';
      })
      .addCase(createFinancement.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteFinancement.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.success = true;
        state.successMessage = 'delete';
      })
      .addCase(deleteFinancement.rejected, (state, action) => { state.error = action.payload; });

    builder
      .addCase(payerTraite.pending, (state) => { state.loading = true; })
      .addCase(payerTraite.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.items.findIndex(i => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
        state.success = true;
        state.successMessage = 'payer';
      })
      .addCase(payerTraite.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(payerPlusieursTraites.pending, (state) => { state.loading = true; })
      .addCase(payerPlusieursTraites.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.items.findIndex(i => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
        // Le toast est géré inline dans le composant
      })
      .addCase(payerPlusieursTraites.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { resetSuccess, clearError } = slice.actions;
export default slice.reducer;
